import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTrial } from '../../contexts/TrialContext';

const pad = (n) => String(n).padStart(2, '0');
/** "66h 45m" while more than an hour is left; "45m 22s" inside the last hour. */
export function fmtLeft(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h > 0 ? `${h}h ${pad(m)}m` : `${m}m ${pad(sec)}s`;
}

/** Header countdown for trial students: how long their trial window has left; red + pulsing in the last hour.
    Ticks once a minute until the last hour (seconds only then) — keeps phones smooth. */
export default function TrialPill() {
  const { user, refreshUser } = useAuth();
  const { cfg, trialMode } = useTrial();
  const t = user?.trial;
  const [now, setNow] = useState(Date.now());
  const active = trialMode && t?.status === 'active' && t?.expires_at;
  const expiresAt = active ? t.expires_at : null;
  useEffect(() => {
    if (!expiresAt) return undefined;
    let id;
    const target = new Date(expiresAt).getTime();
    const schedule = () => {
      const left = target - Date.now();
      id = setTimeout(() => { setNow(Date.now()); schedule(); }, left > 3_600_000 ? 60_000 : 1000);
    };
    schedule();
    return () => clearTimeout(id);
  }, [expiresAt]);
  const left = active ? (new Date(t.expires_at).getTime() - now) / 1000 : 0;
  const ranOut = active && left <= 0;
  // Trial just ran out while the tab was open → pull the new state so the wall appears.
  useEffect(() => {
    if (ranOut) refreshUser?.();
  }, [ranOut, refreshUser]);
  if (!active) return null;
  const urgent = left < 3600;
  return (
    <Link to="/checkout" title="Upgrade to Pro to keep your access"
      className={`inline-flex items-center gap-1 sm:gap-1.5 rounded-full border px-2 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-bold tabular-nums whitespace-nowrap transition-colors ${urgent ? 'bg-rose-600 text-white border-rose-600 animate-pulse' : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'}`}
      data-testid="trial-pill" data-urgent={urgent ? '1' : '0'}>
      {urgent ? <Zap size={12} /> : <Hourglass size={12} />}
      <span className="font-semibold hidden md:inline">{cfg.header_timer_label || 'Trial ends in'}</span>
      <span data-testid="trial-pill-countdown">{fmtLeft(left)}</span>
    </Link>
  );
}
